/**
 * Chat Sync Hook
 *
 * Manages cross-window chat synchronization using BroadcastChannel API.
 * This allows the pop-out chat window and main window to share the same conversation.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { ChatAction, ChatCard } from '../types';

const CHANNEL_NAME = 'danny-chat-sync';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: ChatAction[];
  cards?: ChatCard[];
}

export type ChatSyncMessageType =
  | 'MESSAGES_UPDATE'
  | 'STREAMING_UPDATE'
  | 'LOADING_UPDATE'
  | 'CLEAR'
  | 'SYNC_REQUEST'
  | 'SYNC_RESPONSE'
  | 'CONTEXT_UPDATE'
  | 'NAVIGATE_VIEW'
  | 'SELECT_TASK';

export interface ChatSyncMessage {
  type: ChatSyncMessageType;
  payload: unknown;
  timestamp: number;
  sourceId: string;
}

interface UseChatSyncOptions {
  onMessagesUpdate: (messages: ChatMessage[]) => void;
  onStreamingUpdate: (content: string, toolStatus: string | null) => void;
  onLoadingUpdate: (isLoading: boolean) => void;
  onClear: () => void;
  onSyncRequest: () => void;
  onContextUpdate?: (context: unknown) => void;
}

export function useChatSync(options: UseChatSyncOptions) {
  const windowId = useRef<string>(crypto.randomUUID()).current;
  const channelRef = useRef<BroadcastChannel | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<ChatSyncMessage>) => {
      // Ignore messages from self
      if (event.data.sourceId === windowId) return;

      const opts = optionsRef.current;
      switch (event.data.type) {
        case 'MESSAGES_UPDATE':
          opts.onMessagesUpdate(event.data.payload as ChatMessage[]);
          break;
        case 'STREAMING_UPDATE': {
          const { content, toolStatus } = event.data.payload as {
            content: string;
            toolStatus: string | null;
          };
          opts.onStreamingUpdate(content, toolStatus);
          break;
        }
        case 'LOADING_UPDATE':
          opts.onLoadingUpdate(event.data.payload as boolean);
          break;
        case 'CLEAR':
          opts.onClear();
          break;
        case 'SYNC_REQUEST':
          opts.onSyncRequest();
          break;
        case 'SYNC_RESPONSE':
          opts.onMessagesUpdate(event.data.payload as ChatMessage[]);
          break;
        case 'CONTEXT_UPDATE':
          opts.onContextUpdate?.(event.data.payload);
          break;
        case 'NAVIGATE_VIEW': {
          const { viewSlug } = event.data.payload as { viewSlug: string };
          window.dispatchEvent(
            new CustomEvent('danny-navigate-view', { detail: { viewSlug } }),
          );
          break;
        }
        case 'SELECT_TASK': {
          const { taskId } = event.data.payload as { taskId: string };
          window.dispatchEvent(
            new CustomEvent('danny-select-task', { detail: { taskId } }),
          );
          break;
        }
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [windowId]);

  const broadcast = useCallback(
    (type: ChatSyncMessageType, payload: unknown) => {
      channelRef.current?.postMessage({
        type,
        payload,
        timestamp: Date.now(),
        sourceId: windowId,
      } satisfies ChatSyncMessage);
    },
    [windowId]
  );

  const broadcastMessages = useCallback(
    (messages: ChatMessage[]) => {
      broadcast('MESSAGES_UPDATE', messages);
    },
    [broadcast]
  );

  const broadcastStreaming = useCallback(
    (content: string, toolStatus: string | null) => {
      broadcast('STREAMING_UPDATE', { content, toolStatus });
    },
    [broadcast]
  );

  const broadcastLoading = useCallback(
    (isLoading: boolean) => {
      broadcast('LOADING_UPDATE', isLoading);
    },
    [broadcast]
  );

  const broadcastClear = useCallback(() => {
    broadcast('CLEAR', null);
  }, [broadcast]);

  const requestSync = useCallback(() => {
    broadcast('SYNC_REQUEST', null);
  }, [broadcast]);

  const respondSync = useCallback(
    (messages: ChatMessage[]) => {
      broadcast('SYNC_RESPONSE', messages);
    },
    [broadcast]
  );

  const broadcastContext = useCallback(
    (context: unknown) => {
      broadcast('CONTEXT_UPDATE', context);
    },
    [broadcast]
  );

  type NavigationMessage =
    | { type: 'NAVIGATE_VIEW'; payload: { viewSlug: string } }
    | { type: 'SELECT_TASK'; payload: { taskId: string } };

  const broadcastNavigation = useCallback(
    (msg: NavigationMessage) => {
      broadcast(msg.type, msg.payload);
    },
    [broadcast]
  );

  return {
    windowId,
    broadcastMessages,
    broadcastStreaming,
    broadcastLoading,
    broadcastClear,
    requestSync,
    respondSync,
    broadcastContext,
    broadcastNavigation,
  };
}
